import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StorageManager } from '../utils/storageManager';
import { Holding, MacroState, MarketSignal, MacroIndicators, OptimizationData, WatchlistItem, AutoTradeLog } from '../types';
import { analyzeMacro, getPortfolioAdvice, optimizePortfolio } from '../services/gemini';
import { classifyAIError, AIErrorInfo } from '../utils/aiErrorHandler';
import { MOCK_MACRO, MOCK_SIGNALS, MOCK_HOLDINGS } from '../constants/mockData';
import { calculateSignal, calculateMacroParameters, DEFAULT_NEUTRAL_MACRO, getStandardMarketSession } from '../utils/analysis';
import { fetchHoldingsFromDB, saveHoldingToDB, updateHoldingInDB, deleteHoldingFromDB, fetchMacroIndicators, saveMacroState, fetchMacroState, fetchBulkSignals, fetchBulkPrices, saveHistory, fetchHistory, fetchUserProfile, fetchWatchlistFromDB, saveWatchlistItemToDB, deleteWatchlistItemFromDB, fetchAutoTradeLogs, saveAutoTradeLog } from '../services/api';
import { auth } from '../firebase';
import { sanitizeMacroState } from '../utils/macro_utils';
import { resolveLiveModeFromProfile } from './useLiveTradingMode';

export const getAssetMarket = (symbol: string, market?: string) => {
  if (market === 'US' || market === 'KR') return market;
  const sym = symbol.toUpperCase();
  if (sym === 'USD') return 'US';
  if (sym === 'KRW' || sym === 'CASH') return 'KR';
  if (sym.endsWith('.KS') || sym.endsWith('.KQ')) return 'KR';
  return 'US';
};

export function usePortfolioData(userId?: string) {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [macro, setMacro] = useState<MacroState>(() => sanitizeMacroState(DEFAULT_NEUTRAL_MACRO));
  const macroRef = useRef<MacroState>(macro);

  const addPendingCashFlow = useCallback((amount: number) => {
    if (!userId || amount === 0) return;
    const key = `optix_pending_cash_flow_${userId}`;
    const current = Number(localStorage.getItem(key) || 0);
    localStorage.setItem(key, String(current + amount));
  }, [userId]);

  const consumePendingCashFlow = useCallback((): number => {
    if (!userId) return 0;
    const key = `optix_pending_cash_flow_${userId}`;
    const current = Number(localStorage.getItem(key) || 0);
    if (current !== 0) {
      localStorage.removeItem(key);
    }
    return current;
  }, [userId]);

  // Keep ref in sync
  useEffect(() => {
    macroRef.current = macro;
  }, [macro]);

  const [indicators, setIndicators] = useState<MacroIndicators | null>(null);

  // Atomic helper: always update indicators AND recalculate macro params together.
  // This eliminates the race condition where useEffect fired with stale cached data
  // while fetchMarketData was still processing the fresh response.
  const applyIndicators = useCallback((data: MacroIndicators, market: 'US' | 'KR' | 'ALL' = 'US') => {
    const targetMarket = market === 'ALL' ? 'US' : market as 'US' | 'KR';
    setIndicators(data);
    const macroParams = calculateMacroParameters(data, targetMarket);
    setMacro(prev => ({
      ...prev,
      ...macroParams,
      indicators: data,
      asOfDate: new Date().toISOString().split('T')[0]
    }));
  }, []);
  const [signals, setSignals] = useState<MarketSignal[]>([]);
  const [aiAdvice, setAiAdvice] = useState<string>('');
  const [aiErrorInfo, setAiErrorInfo] = useState<AIErrorInfo | null>(null);
  const [aiProposedTrades, setAiProposedTrades] = useState<MarketSignal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDbError, setIsDbError] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('all');
  const [isIntelligenceLoading, setIsIntelligenceLoading] = useState(false);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [isDeepAnalysisRunning, setIsDeepAnalysisRunning] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState<0 | 1 | 2 | 3>(0);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const lastMarketRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastAttemptedQuickScanRef = useRef<{ indicators: any; aiSummary: string | null }>({ indicators: null, aiSummary: null });

  const [userProfile, setUserProfile] = useState<any>(null);
  const [optimizationData, setOptimizationData] = useState<OptimizationData | null>(null);
  const [autoTradeLogs, setAutoTradeLogs] = useState<AutoTradeLog[]>([]);
  const [isAutoTradeLogsLoading, setIsAutoTradeLogsLoading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationStage, setOptimizationStage] = useState(0); // 0=idle, 1=loading config, 2=running AI, 3=parsing
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [isMacroLoading, setIsMacroLoading] = useState(false);
  const formatKey = userId ? `mmo_${userId}_macro_format` : 'mmo_macro_format';
  const [macroReportFormat, setMacroReportFormatState] = useState<'text' | 'compact' | 'visual' | 'technical'>(() => {
    const saved = StorageManager.safeGet<string>(formatKey, 'visual');
    if (['text', 'compact', 'visual', 'technical'].includes(saved)) {
      return saved as any;
    }
    return 'visual';
  });

  const setMacroReportFormat = useCallback((format: 'text' | 'compact' | 'visual' | 'technical') => {
    setMacroReportFormatState(format);
    StorageManager.safeSet(formatKey, format);
  }, [formatKey]);
  const currencyKey = userId ? `mmo_${userId}_currency_pref` : 'mmo_currency_pref';

  const [displayCurrencyPreference, setDisplayCurrencyPreference] = useState<'USD' | 'KRW'>(() => {
    const saved = StorageManager.safeGet<string>(currencyKey, 'USD');
    if (saved === 'USD' || saved === 'KRW') return saved;
    return 'USD';
  });

  const setCurrencyPreference = (pref: 'USD' | 'KRW') => {
    setDisplayCurrencyPreference(pref);
    StorageManager.safeSet(currencyKey, pref);
  };
  const isFetchingRef = useRef(false);
  const isSignalsFetchingRef = useRef(false);
  const isPollingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const initialLoadInProgressRef = useRef(false);
  const lastLoadedAccountRef = useRef<string | undefined>('__UNINITIALIZED__');

  // Load initial holdings from DB
  // Emergency fallback: force isInitialLoadDone to true after 30s to prevent permanent hang
  useEffect(() => {
    if (userId && userId !== 'guest' && !isInitialLoadDone) {
      const timer = setTimeout(() => {
        console.warn("[Emergency] Initial load taking too long, bypassing...");
        setUserProfile(prev => {
          if (!prev) {
            const cached = StorageManager.safeGet<any>(`mmo_${userId}_profile_cache`, null);
            if (cached) {
              console.info("[Emergency] Recovered userProfile from local cache");
              return cached;
            }
          }
          return prev;
        });
        setIsInitialLoadDone(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [userId, isInitialLoadDone]);

  useEffect(() => {
    if (userId && userId !== 'guest') {
      const cached = StorageManager.safeGet<any>(`mmo_${userId}_profile_cache`, null);
      setIsInitialLoadDone(false);
      setHoldings([]);
      setAiAdvice('');
      setIndicators(null);
      setSignals([]);
      setAiProposedTrades([]);
      setOptimizationData(null);
      setUserProfile(cached);
      setMacro(sanitizeMacroState(DEFAULT_NEUTRAL_MACRO));
    }
  }, [userId]);

  // ─── Enriched User Profile with Dynamic Target Goal Amount ────────────────
  const enrichedUserProfile = useMemo(() => {
    if (!userProfile) return null;
    const market = userProfile.targetMarket || 'US';
    const goalAmount = market === 'KR'
      ? (userProfile.targetGoalAmountKR || 0)
      : (userProfile.targetGoalAmountUS || 0);
    return {
      ...userProfile,
      targetGoalAmount: goalAmount || userProfile.targetGoalAmount || 0
    };
  }, [userProfile]);

  // 🔥 Critical Fix: Synchronously reset loading state when userId changes 
  // to prevent App.tsx from seeing isInitialLoadDone=true from a previous guest session.
  // We use effective variables to ensure the return values are consistent IN THE SAME RENDER
  // that the change is detected, avoiding a one-frame transient mismatch.
  let effectiveLoadDone = isInitialLoadDone;
  let effectiveProfile = enrichedUserProfile;

  if ((userId || null) !== lastUserId) {
    const cachedProfile = (userId && userId !== 'guest') ? StorageManager.safeGet<any>(`mmo_${userId}_profile_cache`, null) : null;
    setIsInitialLoadDone(false);
    setUserProfile(cachedProfile);
    setLastUserId(userId || null);
    effectiveLoadDone = false;
    effectiveProfile = cachedProfile ? {
      ...cachedProfile,
      targetGoalAmount: (cachedProfile.targetMarket === 'KR' ? cachedProfile.targetGoalAmountKR : cachedProfile.targetGoalAmountUS) || cachedProfile.targetGoalAmount || 0
    } : null;
  }

  useEffect(() => {
    if (!userId || userId === 'guest') {
      if (userId === 'guest') {
        const sanitizedMockMacro = sanitizeMacroState(MOCK_MACRO);
        setHoldings(MOCK_HOLDINGS);
        setMacro(sanitizedMockMacro);
        setSignals(MOCK_SIGNALS);
      }
      setIsInitialLoadDone(true);
      return;
    }

    const loadHoldings = async () => {
      // Guard: prevent concurrent initial load executions
      if (initialLoadInProgressRef.current) return;
      initialLoadInProgressRef.current = true;
      try {
        let normalizedProfile = userProfile;

        // Only fetch profile if it's not already loaded
        if (!normalizedProfile) {
          // Wait for auth to settle only on initial load
          let attempts = 0;
          while (!auth.currentUser && userId !== 'guest' && attempts < 5) {
            await new Promise(r => setTimeout(r, 200));
            attempts++;
          }

          console.log("[usePortfolioData] Fetching profile for:", userId);
          const profile = await fetchUserProfile('INIT');
          normalizedProfile = {
            ...profile,
            isApproved: profile?.isApproved === true,
            language: profile?.language || profile?.uiLanguage || 'ko',
            targetMarket: profile?.targetMarket || 'US'
          };
          setUserProfile(normalizedProfile);
        }

        const currentMarket = normalizedProfile.targetMarket || 'US';
        lastMarketRef.current = currentMarket;
        console.log(`[usePortfolioData] Loading data for ${currentMarket}...`);

        // Stop further data fetch if not approved
        if (normalizedProfile.isApproved !== true && userId !== 'guest') {
          setIsInitialLoadDone(true);
          return;
        }

        // 단일 진실원(SSOT): 실거래 모드가 아니면 모의투자 모드로 격리 로드
        const isLive = resolveLiveModeFromProfile(normalizedProfile);
        const isSandbox = !isLive || !!normalizedProfile.activeSandboxMode;

        // 🚀 병렬 실행 최적화: 매크로 지표/히스토리 조회를 홀딩 조회와 병렬로 즉시 시작하여 Waterfall 지연 제거
        const macroFetchPromise = Promise.allSettled([
          fetchMacroIndicators(currentMarket, false),
          fetchMacroState(false),
          fetchHistory()
        ]);

        const [dbHoldings, dbWatchlist] = await Promise.all([
          fetchHoldingsFromDB('ALL', selectedAccountId !== 'all' ? selectedAccountId : undefined),
          fetchWatchlistFromDB('ALL')
        ]);
        setWatchlist(dbWatchlist);
        setHoldings(dbHoldings);

        // 🚀 초기 로딩 차단 해제: 핵심 데이터(프로필, 홀딩, 관심종목)가 준비되면 전체 화면 로딩을 1~2초 내에 즉시 해제
        const initialEffectiveAccount = isSandbox ? 'manual' : (selectedAccountId !== 'all' ? selectedAccountId : undefined);
        lastLoadedAccountRef.current = `${isLive ? 'live' : 'paper'}_${initialEffectiveAccount || 'all'}`;
        setIsInitialLoadDone(true);

        // Fetch signals and FRESH prices in parallel using the freshly loaded symbols
        const allSymbols = Array.from(new Set([...dbHoldings.map(h => h.symbol), ...dbWatchlist.map(w => w.symbol)]));

        if (allSymbols.length > 0) {
          const [dbSignals, freshPrices] = await Promise.all([
            fetchBulkSignals(allSymbols),
            fetchBulkPrices(allSymbols).catch(() => [])
          ]);

          // Pre-map fresh prices for fast lookup (case-insensitive keys)
          const priceMap = new Map(freshPrices.map(p => [p.symbol.toUpperCase(), p]));

          const combinedItems = [
            ...dbHoldings.map(h => ({ symbol: h.symbol, market: h.market, holding: h, name: h.name })),
            ...dbWatchlist.map(w => ({ symbol: w.symbol, market: w.market, holding: undefined, name: w.name }))
          ];

          const uniqueItemsMap = new Map();
          combinedItems.forEach(item => {
            uniqueItemsMap.set(item.symbol.toUpperCase(), item);
          });
          const uniqueItems = Array.from(uniqueItemsMap.values());

          const initialSignals = uniqueItems.map(item => {
            const h = item.holding;
            const dbSig = dbSignals.find(s => s.symbol.toUpperCase() === item.symbol.toUpperCase()) as any;
            const fresh = priceMap.get(item.symbol.toUpperCase());

            // Prioritize fresh price over stored signal data
            const q = dbSig ? (dbSig.data?.quantData || dbSig.data || dbSig) : {};
            const effectivePrice = fresh?.price || q.price || (h ? h.avgCost : 0);
            const effectivePrevClose = fresh?.prevClose || q.prevClose || (h ? h.avgCost : 0);

            const effectiveMarketState = fresh?.marketState || dbSig?.marketState || getStandardMarketSession();

            return calculateSignal(
              item.symbol, effectivePrice, effectivePrevClose,
              q.shortRatio || 0, q.daysToCover || 0, 0,
              macroRef.current, {
              ivRank: (q.ivRank !== undefined && q.ivRank !== null && Number(q.ivRank) > 0) ? Number(q.ivRank) : null,
              putCallRatio: q.putCallRatio,
              gammaFlip: q.gammaFlip,
              maxPain: q.maxPain,
              rvol: q.rvol !== undefined ? Number(q.rvol) : (dbSig?.rvol !== undefined ? Number(dbSig.rvol) : 1.0),
              volumeScore: q.volumeScore !== undefined ? Number(q.volumeScore) : (dbSig?.volumeScore !== undefined ? Number(dbSig.volumeScore) : 50.0),
              cmf: q.cmf !== undefined ? Number(q.cmf) : (dbSig?.cmf !== undefined ? Number(dbSig.cmf) : 0.0),
              volumeSignalType: q.volumeSignalType || dbSig?.volumeSignalType || 'NORMAL',
              avgVolume: q.avgVolume !== undefined ? Number(q.avgVolume) : (dbSig?.avgVolume !== undefined ? Number(dbSig.avgVolume) : 0)
            }, h, undefined, fresh?.timestamp || q.timestamp || dbSig?.updatedAt,
              effectiveMarketState,
              item.name || dbSig?.name
            );
          });
          setSignals(initialSignals);

          // Background refresh for incomplete signals
          const pendingHoldings = initialSignals
            .filter(s => s.isPending)
            .map(s => dbHoldings.find(h => h.symbol.toUpperCase() === s.symbol.toUpperCase()))
            .filter(Boolean) as Holding[];

          if (pendingHoldings.length > 0) {
            fetchSignals(pendingHoldings, false, false, dbWatchlist);
          }
        } else {
          setSignals([]);
        }

        // 병렬로 백그라운드에서 진행되던 매크로 및 히스토리 응답 처리
        const [indicData, savedMacro, historyData] = await macroFetchPromise;

        if (indicData.status === 'fulfilled') {
          applyIndicators(indicData.value, currentMarket);
        }
        if (savedMacro.status === 'fulfilled' && savedMacro.value?.aiSummary) {
          const s: any = { ...savedMacro.value };
          const savedUpdatedAt = s.updatedAt;

          // 🔧 FIX (Stale Spread Injection): The persisted macro state embeds a full
          // snapshot of market indicators (nested `indicators` object + top-level
          // fields such as `yieldSpread`, `government`, ...). That snapshot can be
          // hours/days old — e.g. it carried the discontinued FRED T10Y2Y value of
          // -0.35%p. Spreading `s` over `prev` used to overwrite the fresh data that
          // applyIndicators() just applied above, so the dashboard showed correct
          // bond yields (from the fresh `indicators` state) while the AI intelligence
          // report received the stale -0.35%p 10Y-2Y spread via macroRef.current,
          // cascading into wrong regime/risk analysis. Only AI-generated text and
          // analysis metadata may be restored from persistence; all market data must
          // always come from the fresh fetch.
          if (indicData.status === 'fulfilled' && indicData.value) {
            const staleMarketFields = [
              'indicators', 'riskMetrics', 'regimes', 'marketRegime', 'asOfDate',
              'yieldSpread', 'government', 'economics', 'futures', 'sectors', 'calendars',
              'fedFunds', 'inflation', 'unemployment',
              'sp500', 'nasdaq', 'vix', 'vixeq', 'dxy', 'sox',
              'kospi', 'kosdaq', 'usdkrw', 'vkospi',
              'bitcoin', 'gold', 'silver', 'copper', 'oil', 'naturalGas',
              'russell2000', 'djia', 'm2MoneySupply',
              'ironOre', 'lithium', 'nickel', 'cobalt', 'wheat', 'corn', 'soybeans', 'bdi',
              'isMock', 'lastUpdated', 'updatedAt'
            ];
            for (const f of staleMarketFields) delete s[f];
          }

          setMacro(prev => ({
            ...prev,
            ...s,
            // Re-sync top-level convenience fields from the FRESH indicators so any
            // component reading macro.<field> also sees current values.
            ...(indicData.status === 'fulfilled' && indicData.value ? {
              fedFunds: indicData.value.fedFunds ?? prev.fedFunds,
              inflation: indicData.value.inflation ?? prev.inflation,
              unemployment: indicData.value.unemployment ?? prev.unemployment,
              government: indicData.value.government ?? prev.government,
              ...((indicData.value as any).yieldSpread !== undefined
                ? { yieldSpread: (indicData.value as any).yieldSpread }
                : {}),
            } : {}),
            aiAnalysisDate: s.aiAnalysisDate || savedUpdatedAt || new Date(0).toISOString()
          }));
        }
        if (historyData.status === 'fulfilled' && Array.isArray(historyData.value) && historyData.value.length > 0) {
          const latest = historyData.value[0];
          if (latest.ai_advice) {
            setAiAdvice(latest.ai_advice);
          }
          if (latest.optimization_data) {
            try {
              setOptimizationData(
                typeof latest.optimization_data === 'string'
                  ? JSON.parse(latest.optimization_data)
                  : latest.optimization_data
              );
            } catch (err) {
              console.warn("Failed to parse optimization_data from history:", err);
            }
          }
        }
      } catch (e) {
        console.error("Failed to load holdings", e);
        if (userId !== 'guest') setIsDbError(true);
      } finally {
        setIsInitialLoadDone(true);
        initialLoadInProgressRef.current = false;
      }
    };

    loadHoldings();
  }, [userId]);

  const isLive = resolveLiveModeFromProfile(userProfile);
  const isSandbox = !isLive || !!userProfile?.activeSandboxMode;

  const currentAccountFetchRef = useRef<number>(0);

  useEffect(() => {
    if (!isInitialLoadDone) return;
    // Guard: do not attempt to refetch if userId is not authenticated yet
    if (!userId || userId === 'guest') return;

    const effectiveAccountId = selectedAccountId !== 'all' ? selectedAccountId : undefined;
    const currentKey = `${isLive ? 'live' : 'paper'}_${effectiveAccountId || 'all'}`;

    // 초기 로딩에서 이미 동일한 계좌의 홀딩과 시그널/가격을 가져왔으므로, 계좌나 샌드박스/라이브 상태가 실제로 변경되지 않았다면 중복 재호출을 스킵합니다.
    if (lastLoadedAccountRef.current === currentKey) {
      return;
    }
    lastLoadedAccountRef.current = currentKey;

    const fetchId = ++currentAccountFetchRef.current;
    const refetchHoldings = async () => {
      setIsLoading(true);
      try {
        const dbHoldings = await fetchHoldingsFromDB('ALL', effectiveAccountId);

        // Guard against race conditions if user switches accounts rapidly
        if (fetchId !== currentAccountFetchRef.current) return;

        const safeHoldings = dbHoldings || [];
        setHoldings(safeHoldings);
        if (safeHoldings.length > 0) {
          await fetchSignals(safeHoldings, false);
        }
      } catch (e) {
        console.error("Refetch holdings failed:", e);
      } finally {
        if (fetchId === currentAccountFetchRef.current) {
          setIsLoading(false);
        }
      }
    };

    refetchHoldings();
  }, [selectedAccountId, isInitialLoadDone, userId, isSandbox]);

  const currentExchangeRate = useMemo(() => {
    if (indicators?.usdkrw?.value) return indicators.usdkrw.value;
    return 1350; // Dynamic fallback if indicators not loaded
  }, [indicators]);

  const portfolioStats = useMemo(() => {
    const pref = displayCurrencyPreference;
    const groups = {
      ALL: [] as Holding[],
      US: [] as Holding[],
      KR: [] as Holding[],
    };

    // Defensive guard: holdings should always be an array; filter active holdings (shares > 0)
    const safeHoldings = (Array.isArray(holdings) ? holdings : []).filter(h => Number(h.shares) > 0);

    safeHoldings.forEach(h => {
      const assetMarket = getAssetMarket(h.symbol, h.market);
      groups.ALL.push(h);
      if (assetMarket === 'US') groups.US.push(h);
      if (assetMarket === 'KR') groups.KR.push(h);
    });

    const calculateGroupStats = (groupHoldings: Holding[]) => {
      let groupTotalValue = 0;
      let groupDailyReturnAmt = 0;
      let groupTotalReturnAmt = 0;
      let groupTotalCost = 0;

      // First pass: calculate values and returns in display currency
      const groupEnriched = groupHoldings.map(h => {
        const assetMarket = getAssetMarket(h.symbol, h.market);
        const sig = signals.find(s => s.symbol.toUpperCase() === h.symbol.toUpperCase());
        const CASH_SYMBOLS = ['USD', 'KRW', 'EUR', 'JPY', 'CASH'];
        const isCash = CASH_SYMBOLS.includes(h.symbol.toUpperCase());
        const currentPrice = isCash ? 1 : ((sig?.price && sig.price > 0) ? sig.price : h.avgCost);
        const avgCost = h.avgCost;

        const nativeVal = h.shares * currentPrice;
        const nativeCost = h.shares * avgCost;
        const nativeDailyReturn = h.shares * (isCash ? 0 : (sig?.price && sig?.prevClose ? (sig.price - sig.prevClose) : 0));
        const nativeTotalReturn = h.shares * (isCash ? 0 : (currentPrice - avgCost));

        let displayValue = nativeVal;
        let displayCost = nativeCost;
        let displayDailyReturn = nativeDailyReturn;
        let displayTotalReturn = nativeTotalReturn;

        let displayPrice = currentPrice;
        let displayAvgCost = avgCost;
        let displayPriceCurrency = assetMarket;

        if (pref === 'KRW') {
          if (assetMarket === 'US') {
            displayValue = nativeVal * currentExchangeRate;
            displayCost = nativeCost * currentExchangeRate;
            displayDailyReturn = nativeDailyReturn * currentExchangeRate;
            displayTotalReturn = nativeTotalReturn * currentExchangeRate;
            displayPrice = currentPrice * currentExchangeRate;
            displayAvgCost = avgCost * currentExchangeRate;
            displayPriceCurrency = 'KR';
          }
        } else { // pref === 'USD'
          if (assetMarket === 'KR') {
            displayValue = nativeVal / currentExchangeRate;
            displayCost = nativeCost / currentExchangeRate;
            displayDailyReturn = nativeDailyReturn / currentExchangeRate;
            displayTotalReturn = nativeTotalReturn / currentExchangeRate;
            displayPrice = currentPrice / currentExchangeRate;
            displayAvgCost = avgCost / currentExchangeRate;
            displayPriceCurrency = 'US';
          }
        }

        groupTotalValue += displayValue;
        groupDailyReturnAmt += displayDailyReturn;
        groupTotalReturnAmt += displayTotalReturn;
        groupTotalCost += displayCost;

        let finalName = h.name && h.name !== h.symbol ? h.name : (sig?.name || h.name || h.symbol);
        if (isCash) {
          finalName = h.symbol.toUpperCase() === 'USD' ? 'US Dollar' :
            h.symbol.toUpperCase() === 'KRW' ? 'South Korean Won' :
              h.symbol.toUpperCase() === 'CASH' ? 'Cash' : h.symbol;
        }

        return {
          ...h,
          name: finalName,
          displayValue,
          displayCurrency: (pref === 'KRW' ? 'KR' : 'US') as 'US' | 'KR',
          marketValue: displayValue,
          value: nativeVal,
          currentPrice,
          displayPrice,
          displayPriceCurrency,
          displayAvgCost,
          shortRatio: sig?.shortRatio || 0,
          daysToCover: sig?.daysToCover || 0,
          putCallRatio: sig?.putCallRatio || 0,
          ivRank: (sig?.ivRank !== undefined && sig?.ivRank !== null && Number(sig.ivRank) > 0) ? Number(sig.ivRank) : null,
          gammaFlip: sig?.gammaFlip || 0,
          maxPain: sig?.maxPain || 0,
          returnPct: avgCost > 0 ? ((currentPrice - avgCost) / avgCost) * 100 : 0
        };
      });

      // Second pass: assign correct weights relative to group's total value
      const groupEnrichedWithWeights = groupEnriched.map(h => ({
        ...h,
        weight: groupTotalValue > 0 ? h.displayValue / groupTotalValue : 0
      }));

      // Sort by weight descending
      groupEnrichedWithWeights.sort((a, b) => b.weight - a.weight);

      const dailyReturnPct = (groupTotalValue - groupDailyReturnAmt) > 0
        ? (groupDailyReturnAmt / (groupTotalValue - groupDailyReturnAmt)) * 100
        : 0;

      const totalReturnPct = groupTotalCost > 0
        ? (groupTotalReturnAmt / groupTotalCost) * 100
        : 0;

      return {
        totalValue: groupTotalValue,
        dailyReturnAmt: groupDailyReturnAmt,
        dailyReturnPct,
        totalReturnAmt: groupTotalReturnAmt,
        totalReturnPct,
        enrichedHoldings: groupEnrichedWithWeights
      };
    };

    return {
      ALL: calculateGroupStats(groups.ALL),
      US: calculateGroupStats(groups.US),
      KR: calculateGroupStats(groups.KR),
    };
  }, [holdings, signals, displayCurrencyPreference, currentExchangeRate]);

  // Backward compatible values for the active global mode
  const activeMarket = userProfile?.targetMarket || 'US';
  const totalValue = portfolioStats[activeMarket].totalValue;
  const dailyReturnAmt = portfolioStats[activeMarket].dailyReturnAmt;
  const dailyReturnPct = portfolioStats[activeMarket].dailyReturnPct;
  const totalReturnAmt = portfolioStats[activeMarket].totalReturnAmt;
  const totalReturnPct = portfolioStats[activeMarket].totalReturnPct;
  const enrichedHoldings = portfolioStats[activeMarket].enrichedHoldings;

  const getNormalizedUsdTotalValue = useCallback(() => {
    const rawVal = portfolioStats[activeMarket]?.totalValue || totalValue;
    if (displayCurrencyPreference === 'KRW' && currentExchangeRate > 0) {
      return Math.round((rawVal / currentExchangeRate) * 100) / 100;
    }
    return rawVal;
  }, [portfolioStats, activeMarket, totalValue, displayCurrencyPreference, currentExchangeRate]);

  // Auto-record daily snapshot if initial load is done, user is valid, totalValue > 0, and not already recorded today
  const lastAutoSnapshotDateRef = useRef<string | null>(null);
  const isAutoSnapshottingRef = useRef<boolean>(false);
  useEffect(() => {
    if (!isInitialLoadDone || !userId || userId === 'guest' || totalValue <= 0) return;

    const todayStr = new Date().toISOString().split('T')[0];
    const storageKey = `optix_auto_snapshot_${userId}_${todayStr}`;
    
    let hasSavedToday = false;
    try {
      hasSavedToday = sessionStorage.getItem(storageKey) === 'true';
    } catch {}

    if (lastAutoSnapshotDateRef.current === todayStr || hasSavedToday || isAutoSnapshottingRef.current) {
      return;
    }

    const timer = setTimeout(async () => {
      if (lastAutoSnapshotDateRef.current === todayStr || isAutoSnapshottingRef.current) return;
      isAutoSnapshottingRef.current = true;
      try {
        const effectiveAccountId = isSandbox ? 'manual' : selectedAccountId;
        await saveHistory(macro, aiAdvice || '', getNormalizedUsdTotalValue(), enrichedHoldings, dailyReturnAmt, dailyReturnPct, null, consumePendingCashFlow(), effectiveAccountId || 'all');
        lastAutoSnapshotDateRef.current = todayStr;
        try {
          sessionStorage.setItem(storageKey, 'true');
        } catch {}
        console.log('[usePortfolioData] Auto-saved daily portfolio snapshot for:', todayStr, 'account:', effectiveAccountId || 'all');
      } catch (err) {
        console.warn('[usePortfolioData] Daily auto-snapshot background save failed:', err);
      } finally {
        isAutoSnapshottingRef.current = false;
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isInitialLoadDone, userId, totalValue, enrichedHoldings, dailyReturnAmt, dailyReturnPct, macro, aiAdvice, selectedAccountId, isSandbox, getNormalizedUsdTotalValue]);

  const proposedTrades = useMemo(() => {
    if (aiProposedTrades && aiProposedTrades.length > 0) return aiProposedTrades;
    if (!aiAdvice) return [];

    const markers = ['기존 자산 리밸런싱', 'Existing Asset Rebalancing', 'Rebalancing Suggestions', '리밸런싱 제안'];
    let sectionText = '';
    const lowerText = aiAdvice.toLowerCase();
    for (const marker of markers) {
      if (lowerText.includes(marker.toLowerCase())) {
        const parts = aiAdvice.split(new RegExp(`### .*?${marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*?\\n`, 'i'));
        if (parts.length > 1) {
          sectionText = parts[1].split('###')[0];
          break;
        } else {
          const altSplit = aiAdvice.split(new RegExp(`${marker}`, 'i'));
          if (altSplit.length > 1) {
            sectionText = altSplit[1].split('###')[0];
            break;
          }
        }
      }
    }
    if (!sectionText) return [];

    const lines = sectionText.split('\n');
    const parsedTrades: MarketSignal[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('|-') || trimmed.includes('Ticker') || trimmed.includes('종목')) continue;

      // Check markdown table format: | Ticker | Action | Shares | Price | Rationale |
      if (trimmed.startsWith('|')) {
        const cols = trimmed.split('|').map(c => c.trim()).filter(Boolean);
        if (cols.length >= 3) {
          const symbol = cols[0].replace(/\*\*/g, '').trim().toUpperCase();
          const actionRaw = cols[1].replace(/\*\*/g, '').trim().toUpperCase();
          const sharesRaw = cols[2].replace(/[^0-9.-]/g, '');
          const priceRaw = cols[3] ? cols[3].replace(/[^0-9.-]/g, '') : '0';

          let action: MarketSignal['portfolioAction'] = 'HOLD';
          if (actionRaw.includes('BUY') || actionRaw.includes('매수')) action = 'ADD';
          else if (actionRaw.includes('SELL') || actionRaw.includes('매도')) action = 'REDUCE';
          else if (actionRaw.includes('EXIT') || actionRaw.includes('청산')) action = 'EXIT';

          const shares = parseInt(sharesRaw) || 0;
          const targetPrice = parseFloat(priceRaw) || 0;

          if (symbol && symbol.length <= 10 && !symbol.includes('---') && (action !== 'HOLD' || shares !== 0)) {
            const holding = holdings.find(h => h.symbol.toUpperCase() === symbol);
            const signal = signals.find(s => s.symbol.toUpperCase() === symbol);
            const currentPrice = holding?.currentPrice || signal?.price || targetPrice || 100;

            parsedTrades.push({
              symbol,
              name: holding?.name || signal?.name || symbol,
              price: currentPrice,
              prevClose: currentPrice,
              pctChange: 0,
              shortRatio: 0,
              daysToCover: 0,
              putCallRatio: 0,
              ivRank: null,
              gammaFlip: 0,
              maxPain: 0,
              totalScore: 50,
              alertLevel: 'NORMAL',
              portfolioAction: action,
              marketState: 'REGULAR',
              market: holding?.market || 'US',
              proposedTradeShares: action === 'REDUCE' || action === 'EXIT' ? -Math.abs(shares) : Math.abs(shares),
              buyTargetPrice: action === 'ADD' ? targetPrice : undefined,
              sellTargetPrice: action === 'REDUCE' || action === 'EXIT' ? targetPrice : undefined
            });
          }
        }
      }
      // Check bullet list format: - [Ticker] : [Action] | [Shares] | [Price] | [Rationale]
      else if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
// eslint-disable-next-line
        const match = trimmed.match(/(?:\*|-)\s*(?:\**([A-Z0-9.\-_]+)\**)\s*[:\/-]?\s*(?:([^|]+))\s*\|\s*([^|]+)\s*\|\s*([^|]+)/i);
        if (match) {
          const symbol = match[1].trim().toUpperCase();
          const actionRaw = match[2].trim().toUpperCase();
          const sharesRaw = match[3].replace(/[^0-9.-]/g, '');
          const priceRaw = match[4].replace(/[^0-9.-]/g, '');

          let action: MarketSignal['portfolioAction'] = 'HOLD';
          if (actionRaw.includes('BUY') || actionRaw.includes('매수')) action = 'ADD';
          else if (actionRaw.includes('SELL') || actionRaw.includes('매도')) action = 'REDUCE';
          else if (actionRaw.includes('EXIT') || actionRaw.includes('청산')) action = 'EXIT';

          const shares = parseInt(sharesRaw) || 0;
          const targetPrice = parseFloat(priceRaw) || 0;

          if (symbol && symbol.length <= 10 && (action !== 'HOLD' || shares !== 0)) {
            const holding = holdings.find(h => h.symbol.toUpperCase() === symbol);
            const signal = signals.find(s => s.symbol.toUpperCase() === symbol);
            const currentPrice = holding?.currentPrice || signal?.price || targetPrice || 100;

            parsedTrades.push({
              symbol,
              name: holding?.name || signal?.name || symbol,
              price: currentPrice,
              prevClose: currentPrice,
              pctChange: 0,
              shortRatio: 0,
              daysToCover: 0,
              putCallRatio: 0,
              ivRank: null,
              gammaFlip: 0,
              maxPain: 0,
              totalScore: 50,
              alertLevel: 'NORMAL',
              portfolioAction: action,
              marketState: 'REGULAR',
              market: holding?.market || 'US',
              proposedTradeShares: action === 'REDUCE' || action === 'EXIT' ? -Math.abs(shares) : Math.abs(shares),
              buyTargetPrice: action === 'ADD' ? targetPrice : undefined,
              sellTargetPrice: action === 'REDUCE' || action === 'EXIT' ? targetPrice : undefined
            });
          }
        }
      }
    }
    return parsedTrades;
  }, [aiProposedTrades, aiAdvice, holdings, signals]);

  const refreshProfile = useCallback(async () => {
    if (!userId || userId === 'guest') return;
    try {
      const { fetchUserProfile } = await import('../services/api');
      const profile = await fetchUserProfile('REFRESH');

      // Normalize to ensure isApproved is a boolean and other fields are present
      const normalizedProfile = {
        ...profile,
        isApproved: profile?.isApproved === true,
        language: profile?.language || profile?.uiLanguage || 'ko',
        targetMarket: profile?.targetMarket || 'US'
      };

      setUserProfile(normalizedProfile);
    } catch (e) {
      console.error("Failed to refresh profile", e);
    }
  }, [userId]);

  // 🚀 Live Price Polling (60s interval)
  useEffect(() => {
    if ((holdings.length === 0 && watchlist.length === 0) || !isInitialLoadDone) return;

    const pollPrices = async () => {
      // Skip if already fetching full market data or signals
      if (isFetchingRef.current || isSignalsFetchingRef.current) return;
      // Prevent overlapping polls when a previous poll is still in-flight (slow backend)
      if (isPollingRef.current) return;
      isPollingRef.current = true;

      try {
        const symbols = Array.from(new Set([
          ...holdings.map(h => h.symbol),
          ...watchlist.map(w => w.symbol)
        ]));
        const prices = await fetchBulkPrices(symbols);

        setSignals(prevSignals => prevSignals.map(sig => {
          const fresh = prices.find(p => p.symbol.toUpperCase() === sig.symbol.toUpperCase());
          if (fresh) {
            // Re-calculate signal with fresh price while maintaining other signal properties
            // We use current macro state from ref to avoid hook staleness
            return calculateSignal(
              sig.symbol, fresh.price, fresh.prevClose || sig.prevClose,
              sig.shortRatio, sig.daysToCover, 0,
              macroRef.current, {
              ivRank: sig.ivRank,
              putCallRatio: sig.putCallRatio,
              gammaFlip: sig.gammaFlip,
              maxPain: sig.maxPain,
              rvol: sig.rvol,
              volumeScore: sig.volumeScore,
              cmf: sig.cmf,
              volumeSignalType: sig.volumeSignalType,
              avgVolume: sig.avgVolume
            },
              holdings.find(h => h.symbol.toUpperCase() === sig.symbol.toUpperCase()),
              undefined,
              fresh.timestamp || new Date().toISOString(),
              fresh.marketState || getStandardMarketSession()
            );
          }
          return sig;
        }));

        setLastUpdated(new Date().toLocaleTimeString());
      } catch (e) {
        console.warn("[Polling] Price poll failed", e);
      } finally {
        isPollingRef.current = false;
      }
    };

    const interval = setInterval(pollPrices, 60000);
    return () => clearInterval(interval);
  }, [holdings, watchlist, isInitialLoadDone]);

  const fetchSignals = async (targetHoldings: Holding[], forceRefresh: boolean = false, replace: boolean = false, targetWatchlist: WatchlistItem[] = watchlist) => {
    const allSymbols = Array.from(new Set([
      ...targetHoldings.map(h => h.symbol),
      ...targetWatchlist.map(w => w.symbol)
    ]));
    if (isSignalsFetchingRef.current || allSymbols.length === 0) return;

    isSignalsFetchingRef.current = true;
    try {
      const [data, freshPrices] = await Promise.all([
        fetchBulkSignals(allSymbols, forceRefresh),
        fetchBulkPrices(allSymbols, forceRefresh).catch(() => [])
      ]);
      console.log("[fetchSignals] Fetched data:", JSON.stringify(data));
      const priceMap = new Map(freshPrices.map(p => [p.symbol.toUpperCase(), p]));

      setSignals(prev => {
        const next = replace ? [] : [...prev];
        data.forEach(sigData => {
          const idx = next.findIndex(s => s.symbol.toUpperCase() === sigData.symbol.toUpperCase());
          const h = targetHoldings.find(hold => hold.symbol.toUpperCase() === sigData.symbol.toUpperCase());
          const w = targetWatchlist.find(watch => watch.symbol.toUpperCase() === sigData.symbol.toUpperCase());

          if (h || w) {
            const q: any = (sigData as any).data?.quantData || (sigData as any).data || sigData;
            const freshPrice = priceMap.get(sigData.symbol.toUpperCase());

            const existingSig = idx >= 0 ? next[idx] : null;
            const fallbackPrice = existingSig?.price || 0;
            const fallbackPrevClose = existingSig?.prevClose || 0;

            const effectivePrice = freshPrice?.price || ((q.price && q.price > 0) ? q.price : fallbackPrice);
            const effectivePrevClose = freshPrice?.prevClose || ((q.prevClose && q.prevClose > 0) ? q.prevClose : fallbackPrevClose);
            const effectiveMarketState = freshPrice?.marketState || (sigData as any).marketState || getStandardMarketSession();

            const newSig = calculateSignal(
              sigData.symbol,
              effectivePrice,
              effectivePrevClose,
              q.shortRatio, q.daysToCover, 0,
              macroRef.current, {
              ivRank: (q.ivRank !== undefined && q.ivRank !== null && Number(q.ivRank) > 0) ? Number(q.ivRank) : null,
              putCallRatio: q.putCallRatio,
              gammaFlip: q.gammaFlip,
              maxPain: q.maxPain,
              rvol: q.rvol !== undefined ? Number(q.rvol) : (sigData.rvol !== undefined ? Number(sigData.rvol) : (existingSig?.rvol || 1.0)),
              volumeScore: q.volumeScore !== undefined ? Number(q.volumeScore) : (sigData.volumeScore !== undefined ? Number(sigData.volumeScore) : (existingSig?.volumeScore || 50.0)),
              cmf: q.cmf !== undefined ? Number(q.cmf) : (sigData.cmf !== undefined ? Number(sigData.cmf) : (existingSig?.cmf || 0.0)),
              volumeSignalType: q.volumeSignalType || sigData.volumeSignalType || existingSig?.volumeSignalType || 'NORMAL',
              avgVolume: q.avgVolume !== undefined ? Number(q.avgVolume) : (sigData.avgVolume !== undefined ? Number(sigData.avgVolume) : (existingSig?.avgVolume || 0))
            }, h, undefined, freshPrice?.timestamp || q.timestamp || (sigData as any).updatedAt,
              effectiveMarketState,
              (sigData as any).name || w?.name
            );

            if (idx >= 0) next[idx] = newSig;
            else next.push(newSig);
          }
        });
        return next;
      });

      setLastUpdated(new Date().toLocaleTimeString());
    } finally {
      isSignalsFetchingRef.current = false;
    }
  };

  const fetchMarketData = async (forceRefresh: boolean = false) => {
    if (isFetchingRef.current) return;

    // Throttle: prevent multiple clicks within 2 seconds
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 2000) return;
    lastFetchTimeRef.current = now;

    setIsLoading(true);
    isFetchingRef.current = true;
    try {
      const currentMarket = userProfile?.targetMarket || 'US';
      // Run both fetches concurrently to prevent one failure from blocking the other
      const [indicData] = await Promise.all([
        fetchMacroIndicators(currentMarket, forceRefresh).catch(e => {
          console.error("Macro fetch failed:", e);
          return null;
        }),
        (async () => {
          let currentHoldings = holdings;
          let currentWatchlist = watchlist;

          if (forceRefresh) {
            const isSandbox = !!((userProfile?.membershipTier === 'TIER1' || userProfile?.membershipTier === 'FREE' || !userProfile?.membershipTier) && userProfile?.activeSandboxMode);
            try {
              const effectiveAccId = isSandbox ? 'manual' : (selectedAccountId !== 'all' ? selectedAccountId : undefined);
              const [dbHoldings, dbWatchlist] = await Promise.all([
                fetchHoldingsFromDB('ALL', effectiveAccId),
                fetchWatchlistFromDB('ALL')
              ]);
              setHoldings(dbHoldings);
              setWatchlist(dbWatchlist);
              currentHoldings = dbHoldings;
              currentWatchlist = dbWatchlist;
            } catch (dbErr) {
              console.error("Holdings fetch failed during refresh:", dbErr);
            }
          }

          const allSymbolsCount = currentHoldings.length + currentWatchlist.length;
          if (allSymbolsCount > 0) {
            await fetchSignals(currentHoldings, forceRefresh, false, currentWatchlist);
          }
        })().catch(e => {
          console.error("Signals fetch failed:", e);
        })
      ]);

      if (indicData) {
        applyIndicators(indicData, currentMarket);
      }
    } catch (e) {
      console.error("Failed to fetch market data", e);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Listen for app:refresh events (dispatched by MyPageView sync handlers etc.)
  // to refresh market data without a full page reload.
  useEffect(() => {
    const handleAppRefresh = () => {
      console.log("[usePortfolioData] app:refresh event received, refreshing market data...");
      fetchMarketData(true);
    };
    window.addEventListener('app:refresh', handleAppRefresh);
    return () => window.removeEventListener('app:refresh', handleAppRefresh);
  }, [fetchMarketData]);

  const fetchFullMacro = useCallback(async (forceRefresh: boolean = false) => {
    setIsMacroLoading(true);
    try {
      const market = userProfile?.targetMarket || 'US';
      const fullMacro = await fetchMacroState(true, market);
      if (fullMacro) {
        if (!fullMacro.aiSummary) {
          lastAttemptedQuickScanRef.current = { indicators: null, aiSummary: null };
        }
        setMacro(prev => ({
          ...prev,
          ...fullMacro,
          aiAnalysisDate: fullMacro.aiAnalysisDate || fullMacro.updatedAt || new Date().toISOString()
        }));
      }
    } catch (e) {
      console.error("Full macro fetch failed", e);
    } finally {
      setIsMacroLoading(false);
    }
  }, [userProfile]);

  const generateQuickSummary = useCallback(async (forceRefresh: boolean = false) => {
    if (!indicators || isAnalysisRunning) return;

    // Safeguard: Prevent auto-generating multiple times if already attempted for the same indicators and summary state (unless forced)
    if (!forceRefresh &&
      lastAttemptedQuickScanRef.current.indicators === indicators &&
      lastAttemptedQuickScanRef.current.aiSummary === macro.aiSummary &&
      !!macro.aiSummary) {
      console.log("[QuickScan] Skip auto-generation: already attempted for current indicators and summary state");
      return;
    }

    setMacro(prev => ({ 
      ...prev, 
      ...(forceRefresh ? { aiSummary: null } : {}),
      aiAnalysisError: undefined 
    }));
    
    lastAttemptedQuickScanRef.current = { indicators, aiSummary: forceRefresh ? null : macro.aiSummary };

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsAnalysisRunning(true);
    setAnalysisProgress(10);
    setAnalysisStage(1);

    const market = userProfile?.targetMarket || 'US';
    const lang = userProfile?.uiLanguage || 'ko';
    
    let fullSummary = '';

    try {
      await analyzeMacro(indicators, lang, (chunk) => {
        fullSummary += chunk;
        setMacro(prev => ({
          ...prev,
          aiSummary: fullSummary,
          aiAnalysisDate: new Date().toISOString()
        }));
        // Gradual, smooth progress bar updates during stream
        setAnalysisProgress(prev => Math.min(95, prev + 0.5));
      }, signal, macroReportFormat, market);

      setAnalysisProgress(100);

      // Persist to DB (current state)
      await saveMacroState({
        ...macroRef.current,
        aiSummary: fullSummary,
        aiAnalysisDate: new Date().toISOString()
      }, market);
    } catch (e: any) {
      if (e?.name === 'AbortError' || e?.message?.includes('aborted')) {
        console.log("Quick summary generation aborted by user");
      } else {
        console.error("Quick summary generation failed", e);
        
        // Graceful Degradation: Use the local variable to bypass async state lag
        if (fullSummary.length > 50) {
          console.warn("Stream interrupted, but keeping partial summary.");
          const appendMsg = lang === 'ko' 
            ? '\n\n> ⚠️ **안내**: AI 응답이 도중에 중단되었거나 불완전하게 종료되었습니다.'
            : '\n\n> ⚠️ **Note**: AI response was interrupted or terminated incompletely.';
            
          setMacro(prev => ({
            ...prev,
            aiSummary: fullSummary + appendMsg
            // DO NOT set aiAnalysisError to prevent wiping out the UI
          }));
        } else {
          // If we got almost nothing, show the full error screen
          const errorMsg = e?.message || '분석 엔진이 중단되었습니다. 다시 시도해 주세요.';
          setMacro(prev => ({
            ...prev,
            aiAnalysisError: errorMsg
          }));
        }
      }
    } finally {
      if (signal.aborted) {
        setIsAnalysisRunning(false);
        setAnalysisProgress(0);
      } else {
        setTimeout(() => {
          setIsAnalysisRunning(false);
          setAnalysisProgress(0);
        }, 1000);
      }
    }
  }, [indicators, isAnalysisRunning, macroReportFormat, userProfile, macro]);

  const fetchDeepIntelligence = useCallback(async (force: boolean = false) => {
    if ((isAnalysisRunning && !force) || !indicators) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsAnalysisRunning(true);
    setIsDeepAnalysisRunning(true);
    setAnalysisStage(1);
    setAnalysisProgress(5);
    
    // Clear previous errors
    setMacro(prev => ({
      ...prev,
      aiAnalysisError: undefined,
      aiIntelligenceError: undefined
    }));

    let currentStage = 1;
    let macroReportText = '';
    let deepIntelligenceText = '';
    let adviceText = '';
    
    try {
      const market = userProfile?.targetMarket || 'US';
      const lang = userProfile?.uiLanguage || 'ko';

      // Step 1: Macro Analysis (Quick Scan)
      setAnalysisProgress(10);
      await analyzeMacro(indicators, lang, (chunk) => {
        macroReportText += chunk;
        setMacro(prev => ({
          ...prev,
          aiSummary: macroReportText,
          aiAnalysisDate: new Date().toISOString()
        }));
        setAnalysisProgress(prev => Math.min(29, prev + 0.3));
      }, signal, macroReportFormat, market);
      setAnalysisProgress(30);

      // Step 2: Deep Strategic Intelligence
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      currentStage = 2;
      setAnalysisStage(2);
      setAnalysisProgress(35);

      // 2a. Aggregate raw intelligence data (News, Social, etc)
      const { aggregateIntelligence, analyzeIntelligence } = await import('../services/gemini');
      const rawIntelligence = await aggregateIntelligence();
      setAnalysisProgress(45);

      // 2b. Analyze the intelligence
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      await analyzeIntelligence(macroRef.current, rawIntelligence, lang, (chunk) => {
        deepIntelligenceText += chunk;
        setMacro(prev => ({
          ...prev,
          aiIntelligence: deepIntelligenceText
        }));
        setAnalysisProgress(prev => Math.min(69, prev + 0.3));
      }, macroReportText, signal, macroReportFormat, market);
      setAnalysisProgress(70);

      // Step 3: Portfolio Strategic Advice
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      currentStage = 3;
      setAnalysisStage(3);
      setAnalysisProgress(75);
      await getPortfolioAdvice(enrichedHoldings, macroRef.current, lang, (chunk) => {
        adviceText += chunk;
        setAiAdvice(adviceText);
        setAnalysisProgress(prev => Math.min(98, prev + 0.3));
      }, signal, macroReportText, enrichedUserProfile?.targetGoalAmount, enrichedUserProfile?.investorType || 3, market, macroReportFormat);

      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const finalMacro = {
        ...macroRef.current,
        aiSummary: macroReportText,
        aiIntelligence: deepIntelligenceText,
        aiAnalysisDate: new Date().toISOString()
      };

      setAnalysisProgress(100);

      // Persist to DB
      await saveMacroState(finalMacro, market);
      await saveHistory(finalMacro, adviceText, getNormalizedUsdTotalValue(), enrichedHoldings, dailyReturnAmt, dailyReturnPct, null, consumePendingCashFlow());

    } catch (e: any) {
      if (e?.name === 'AbortError' || e?.message?.includes('aborted')) {
        console.log("Deep intelligence fetch aborted by user");
      } else {
        console.error("Deep intelligence fetch failed", e);
        const errorMsg = e?.message || '분석 엔진이 중단되었습니다. 다시 시도해 주세요.';
        const appendMsg = (userProfile?.uiLanguage || 'ko') === 'ko' 
            ? '\n\n> ⚠️ **안내**: AI 응답이 도중에 중단되었거나 불완전하게 종료되었습니다.'
            : '\n\n> ⚠️ **Note**: AI response was interrupted or terminated incompletely.';

        setMacro(prev => {
          const next = { ...prev };
          if (currentStage === 1) {
            if (macroReportText.length > 50) {
              next.aiSummary = macroReportText + appendMsg;
            } else {
              next.aiAnalysisError = errorMsg;
            }
          } else if (currentStage === 2) {
            if (deepIntelligenceText.length > 50) {
              next.aiIntelligence = deepIntelligenceText + appendMsg;
            } else {
              next.aiIntelligenceError = errorMsg;
            }
          }
          return next;
        });

        if (currentStage === 3) {
           if (adviceText.length > 50) {
              setAiAdvice(adviceText + appendMsg);
           } else {
              setAiAdvice(errorMsg);
           }
        }
      }
    } finally {
      if (signal.aborted) {
        setIsAnalysisRunning(false);
        setIsDeepAnalysisRunning(false);
        setAnalysisProgress(0);
      } else {
        setTimeout(() => {
          setIsAnalysisRunning(false);
          setIsDeepAnalysisRunning(false);
          setAnalysisProgress(0);
        }, 1000);
      }
    }
  }, [holdings, enrichedHoldings, signals, indicators, macro, isAnalysisRunning, userId, totalValue, dailyReturnAmt, dailyReturnPct, macroReportFormat, enrichedUserProfile]);

  const fetchIntelligenceOnly = useCallback(async (force: boolean = false) => {
    if ((isDeepAnalysisRunning && !force) || !indicators) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsDeepAnalysisRunning(true);
    setAnalysisStage(2);
    setAnalysisProgress(15);

    try {
      const market = userProfile?.targetMarket || 'US';
      const lang = userProfile?.uiLanguage || 'ko';

      setAnalysisProgress(35);
      const { aggregateIntelligence, analyzeIntelligence } = await import('../services/gemini');
      const rawIntelligence = await aggregateIntelligence();
      setAnalysisProgress(50);

      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      let deepIntelligenceText = '';
      await analyzeIntelligence(macroRef.current, rawIntelligence, lang, (chunk) => {
        deepIntelligenceText += chunk;
        setMacro(prev => ({
          ...prev,
          aiIntelligence: deepIntelligenceText
        }));
        setAnalysisProgress(prev => Math.min(95, prev + 0.5));
      }, macroRef.current.aiSummary || '', signal, macroReportFormat, market);

      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const finalMacro = {
        ...macroRef.current,
        aiIntelligence: deepIntelligenceText,
        aiAnalysisDate: new Date().toISOString()
      };

      setAnalysisProgress(100);
      await saveMacroState(finalMacro, market);
    } catch (e: any) {
      if (e?.name === 'AbortError' || e?.message?.includes('aborted')) {
        console.log("Deep intelligence fetch aborted by user");
      } else {
        console.error("Deep intelligence fetch failed", e);
        const errorMsg = e?.message || '인텔리전스 분석 엔진이 중단되었습니다. 다시 시도해 주세요.';
        setMacro(prev => ({
          ...prev,
          aiIntelligenceError: errorMsg
        }));
      }
    } finally {
      if (signal.aborted) {
        setIsDeepAnalysisRunning(false);
        setAnalysisProgress(0);
      } else {
        setTimeout(() => {
          setIsDeepAnalysisRunning(false);
          setAnalysisProgress(0);
        }, 1000);
      }
    }
  }, [indicators, isDeepAnalysisRunning, macroReportFormat, userProfile]);

  const generateAiStrategy = async () => {
    if (!indicators) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Clear previous error
    setAiErrorInfo(null);
    setIsIntelligenceLoading(true);
    setAnalysisStage(1);
    setAnalysisProgress(15);
    try {
      const market = userProfile?.targetMarket || 'US';
      const lang = userProfile?.uiLanguage || 'ko';
      let adviceText = '';

      setAnalysisStage(2);
      setAnalysisProgress(35);

      await getPortfolioAdvice(enrichedHoldings, macro, lang, (chunk) => {
        adviceText += chunk;
        setAiAdvice(adviceText);
        setAnalysisProgress(prev => Math.min(95, prev + 0.5));
      }, signal, macro.aiSummary, enrichedUserProfile?.targetGoalAmount, enrichedUserProfile?.investorType || 3, market, macroReportFormat);

      setAnalysisStage(3);
      setAnalysisProgress(100);
      // Save to history
      await saveHistory(macro, adviceText, getNormalizedUsdTotalValue(), enrichedHoldings, dailyReturnAmt, dailyReturnPct, null, consumePendingCashFlow());
    } catch (e: any) {
      if (e?.name === 'AbortError' || e?.message?.includes('aborted')) {
        console.log("AI strategy generation aborted by user");
      } else {
        console.error("AI strategy generation failed", e);
        // Classify error and set UI state for user feedback
        const lang = userProfile?.uiLanguage || 'ko';
        const errorInfo = classifyAIError(e, lang);
        setAiErrorInfo(errorInfo);
      }
    } finally {
      setIsIntelligenceLoading(false);
      setAnalysisProgress(0);
    }
  };

  const stopAiAnalysis = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsAnalysisRunning(false);
    setIsDeepAnalysisRunning(false);
    setAnalysisProgress(0);
  }, []);

  const adjustCashBalance = async (market: 'US' | 'KR', amountChange: number) => {
    if (amountChange === 0) return;
    const cashSymbol = market === 'KR' ? 'KRW' : 'USD';
    const cashName = market === 'KR' ? 'Korean Won' : 'US Dollar';

    // 🛡️ 먼저 지정된 market으로 조회, 없으면 'ALL'로 fallback하여 현금 자산 찾기
    const dbHoldings = await fetchHoldingsFromDB(market);
    let existingCash = dbHoldings.find(h => h.symbol.toUpperCase() === cashSymbol.toUpperCase());

    // 지정 market에 현금이 없으면 전체 market에서 찾아보기
    if (!existingCash) {
      const allHoldings = await fetchHoldingsFromDB('ALL');
      existingCash = allHoldings.find(h => h.symbol.toUpperCase() === cashSymbol.toUpperCase());
      if (existingCash) {
        // 다른 market에 현금이 있으면 해당 market으로 업데이트
        const newShares = Math.max(0, (existingCash.shares || 0) + amountChange);
        await updateHoldingInDB(cashSymbol, { shares: newShares }, existingCash.market || market);
        return;
      }
    }

    if (existingCash) {
      const newShares = Math.max(0, (existingCash.shares || 0) + amountChange);
      await updateHoldingInDB(cashSymbol, { shares: newShares }, market);
    } else {
      if (amountChange > 0) {
        await saveHoldingToDB({
          symbol: cashSymbol,
          name: cashName,
          shares: amountChange,
          avgCost: 1,
          targetWeight: 0.1,
          market
        });
      }
    }
  };

  const addWatchlist = async (symbol: string, name?: string, market?: string, notes?: string) => {
    const activeMarket = market || userProfile?.targetMarket || 'US';
    const item = { symbol, name: name || symbol, market: activeMarket as 'US' | 'KR', notes };
    await saveWatchlistItemToDB(item);

    const dbWatchlist = await fetchWatchlistFromDB('ALL');
    setWatchlist(dbWatchlist);

    const dbHoldings = await fetchHoldingsFromDB('ALL');
    await fetchSignals(dbHoldings, true, false, dbWatchlist);
    await refreshProfile();
  };

  const removeWatchlist = async (symbol: string, market: string = 'US') => {
    await deleteWatchlistItemFromDB(symbol, market);

    const dbWatchlist = await fetchWatchlistFromDB('ALL');
    setWatchlist(dbWatchlist);

    const dbHoldings = await fetchHoldingsFromDB('ALL');
    await fetchSignals(dbHoldings, true, false, dbWatchlist);
    await refreshProfile();
  };

  const addHolding = async (h: Omit<Holding, 'id'>, fee: number = 0) => {
    const market = h.market || userProfile?.targetMarket || 'US';
    const accountId = isSandbox ? 'manual' : (h.account_id || 'manual');
    const isCash = h.symbol.toUpperCase() === 'USD' || h.symbol.toUpperCase() === 'KRW' || h.symbol.toUpperCase() === 'CASH';
    if (isCash) {
       let amount = Number(h.shares) || 0;
       if (h.symbol.toUpperCase() === 'USD' && displayCurrencyPreference === 'KRW') amount *= currentExchangeRate;
       if ((h.symbol.toUpperCase() === 'KRW' || h.symbol.toUpperCase() === 'CASH') && displayCurrencyPreference === 'USD') amount /= currentExchangeRate;
       addPendingCashFlow(amount);
    }

    // 기 보유 종목 여부 확인 및 수량/평단가 합산 계산
    const existing = holdings.find(
      item => item.symbol.toUpperCase() === h.symbol.toUpperCase() &&
        (isSandbox || !item.account_id || item.account_id === accountId || accountId === 'manual')
    );

    if (existing && Number(existing.shares) > 0) {
      const existingShares = Number(existing.shares) || 0;
      const existingAvgCost = Number(existing.avgCost) || 0;
      const addedShares = Number(h.shares) || 0;
      const addedAvgCost = Number(h.avgCost) || 0;
      const totalShares = existingShares + addedShares;
      const weightedAvgCost = totalShares > 0
        ? ((existingShares * existingAvgCost) + (addedShares * addedAvgCost)) / totalShares
        : addedAvgCost;

      await updateHoldingInDB(
        h.symbol,
        {
          shares: totalShares,
          avgCost: Number(weightedAvgCost.toFixed(4)),
          market,
          name: h.name || existing.name
        },
        market,
        accountId
      );
    } else {
      await saveHoldingToDB({ ...h, market, account_id: accountId });
    }

    if (fee > 0) {
      await adjustCashBalance(market, -fee);
    }
    const dbHoldings = await fetchHoldingsFromDB('ALL', isSandbox ? 'manual' : undefined);
    setHoldings(dbHoldings);
    await fetchSignals(dbHoldings, true);
    await refreshProfile();
  };

  const addBulkHoldings = async (assets: Array<{ symbol: string, shares: number, name?: string, market?: string }>) => {
    const activeMarket = userProfile?.targetMarket || 'US';
    const accountId = isSandbox ? 'manual' : 'manual';
    setIsLoading(true);
    try {
      await Promise.all(assets.map(a => {
        const m = a.market || activeMarket;
        return saveHoldingToDB({
          symbol: a.symbol,
          name: a.name,
          shares: a.shares,
          avgCost: 0,
          targetWeight: 0,
          market: m,
          account_id: accountId
        });
      }));
      const dbHoldings = await fetchHoldingsFromDB('ALL', isSandbox ? 'manual' : undefined);
      setHoldings(dbHoldings);
      await fetchSignals(dbHoldings, true);
      await refreshProfile();
    } catch (e) {
      console.error("Bulk add failed", e);
    } finally {
      setIsLoading(false);
    }
  };

  const updateHolding = async (s: string, h: Partial<Holding>, fee: number = 0) => {
    const holding = holdings.find(item => item.symbol.toUpperCase() === s.toUpperCase());
    
    if (holding) {
       const isCash = s.toUpperCase() === 'USD' || s.toUpperCase() === 'KRW' || s.toUpperCase() === 'CASH';
       if (isCash && h.shares !== undefined) {
          let diff = Number(h.shares) - Number(holding.shares);
          if (s.toUpperCase() === 'USD' && displayCurrencyPreference === 'KRW') diff *= currentExchangeRate;
          if ((s.toUpperCase() === 'KRW' || s.toUpperCase() === 'CASH') && displayCurrencyPreference === 'USD') diff /= currentExchangeRate;
          addPendingCashFlow(diff);
       }
    }

    const market = h.market || holding?.market || userProfile?.targetMarket || 'US';
    const accountId = isSandbox ? 'manual' : (holding?.account_id || 'manual');
    await updateHoldingInDB(s, h, market, accountId);
    if (fee > 0) {
      await adjustCashBalance(market, -fee);
    }
    const dbHoldings = await fetchHoldingsFromDB('ALL', isSandbox ? 'manual' : undefined);
    setHoldings(dbHoldings);
    await fetchSignals(dbHoldings, true);
    await refreshProfile();
  };

  const calculateReturn = (holding: Holding) => {
    const signal = signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase());
    const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
    // If we have a valid signal price > 0, use it. Otherwise, use avgCost so it doesn't break math, but UI can show Syncing state.
    const currentPrice = isCash ? 1 : ((signal?.price && signal.price > 0) ? signal.price : holding.avgCost);
    const returnPct = isCash ? 0 : ((currentPrice - holding.avgCost) / holding.avgCost) * 100;
    return { price: currentPrice, pct: returnPct };
  };

  const liquidateHolding = async (s: string, sharesToClose: number, salePrice: number, fee: number = 0) => {
    try {
      const holding = holdings.find(item => item.symbol.toUpperCase() === s.toUpperCase());
      const market = holding?.market || userProfile?.targetMarket || 'US';
      if (!holding) {
        console.error(`[liquidateHolding] Holding ${s} not found`);
        return;
      }

      // 🛡️ salePrice가 0이면 signal 가격 또는 avgCost로 fallback
      let effectiveSalePrice = salePrice;
      if (effectiveSalePrice <= 0) {
        const signal = signals.find(sig => sig.symbol.toUpperCase() === s.toUpperCase());
        effectiveSalePrice = (signal?.price && signal.price > 0) ? signal.price : (holding.avgCost || 0);
      }
      if (effectiveSalePrice <= 0) {
        console.error(`[liquidateHolding] Cannot liquidate ${s}: salePrice=0, signal.price=0, avgCost=0`);
        return;
      }

      // 매도 대금 전액을 현금에 반영 (차익만 반영하면 원금이 증발함)
      const totalProceeds = effectiveSalePrice * sharesToClose - fee;
      await adjustCashBalance(market, totalProceeds);

      // ★ accountId 결정: isSandbox면 'manual', 아니면 holding의 account_id (없으면 'manual')
      //    재조회 시에도 동일한 accountId를 사용하여 일관성 유지
      const accountId = isSandbox ? 'manual' : (holding.account_id || 'manual');
      // ★ 재조회용 accountId: isSandbox가 아니면 undefined(=aggregate 모드)로 모든 account 조회
      const refetchAccountId = isSandbox ? 'manual' : undefined;

      if (sharesToClose >= holding.shares) {
        // Fully liquidated - delete the holding
        await deleteHoldingFromDB(s, market, accountId);
      } else {
        // Partially liquidated - deduct shares
        await updateHoldingInDB(s, { shares: holding.shares - sharesToClose }, market, accountId);
      }

      const dbHoldings = await fetchHoldingsFromDB('ALL', refetchAccountId);
      setHoldings(dbHoldings);
      await fetchSignals(dbHoldings, true);
      await refreshProfile();
    } catch (e) {
      console.error(`[liquidateHolding] Failed to liquidate ${s}:`, e);
      throw e; // 호출자(caller)가 에러를 처리할 수 있도록 rethrow
    }
  };

  const deleteHolding = async (s: string) => {
    const holding = holdings.find(item => item.symbol.toUpperCase() === s.toUpperCase());
    
    if (holding) {
       const isCash = s.toUpperCase() === 'USD' || s.toUpperCase() === 'KRW' || s.toUpperCase() === 'CASH';
       if (isCash) {
          let diff = -Number(holding.shares);
          if (s.toUpperCase() === 'USD' && displayCurrencyPreference === 'KRW') diff *= currentExchangeRate;
          if ((s.toUpperCase() === 'KRW' || s.toUpperCase() === 'CASH') && displayCurrencyPreference === 'USD') diff /= currentExchangeRate;
          addPendingCashFlow(diff);
       }
    }

    const market = holding?.market || userProfile?.targetMarket || 'US';
    const accountId = isSandbox ? 'manual' : (holding?.account_id || 'manual');
    await deleteHoldingFromDB(s, market, accountId);
    setHoldings(prev => prev.filter(h => h.symbol.toUpperCase() !== s.toUpperCase()));
    setSignals(prev => prev.filter(sig => sig.symbol.toUpperCase() !== s.toUpperCase()));
    await refreshProfile();
  };

  const changeMarketMode = useCallback(async (market: 'US' | 'KR') => {
    setIsLoading(true);
    try {
      // 1. Fetch indicators for the new market
      const indicData = await fetchMacroIndicators(market, true);
      applyIndicators(indicData, market);

      // 2. Fetch holdings for the new market while preserving selected account
      const effectiveAccId = isSandbox ? 'manual' : (selectedAccountId !== 'all' ? selectedAccountId : undefined);
      const dbHoldings = await fetchHoldingsFromDB('ALL', effectiveAccId);
      setHoldings(dbHoldings);

      // 3. Fetch signals for the new holdings (replace=true to clear stale data)
      if (dbHoldings.length > 0) {
        await fetchSignals(dbHoldings, true, true);
      } else {
        setSignals([]);
      }

      // 4. Update user profile target market
      if (userProfile) {
        const nextProfile = { ...userProfile, targetMarket: market };
        setUserProfile(nextProfile);

        // 🔥 Synchronize currency preference with market mode
        const nextCurrency = (market === 'KR') ? 'KRW' : 'USD';
        setDisplayCurrencyPreference(nextCurrency);

        const { updateUserProfile } = await import('../services/api');
        await updateUserProfile({
          targetMarket: market,
          displayCurrency: nextCurrency
        });
      }

      // 5. Reset quick scan attempt ref to allow re-generation on mode switch
      lastAttemptedQuickScanRef.current = { indicators: null, aiSummary: null };

      // 6. Fetch full macro state for the new market to load or clear AI summary
      const fullMacro = await fetchMacroState(true, market);
      if (fullMacro) {
        setMacro(prev => ({
          ...prev,
          aiSummary: fullMacro.aiSummary || '',
          aiIntelligence: fullMacro.aiIntelligence || '',
          aiAnalysisDate: fullMacro.aiAnalysisDate || fullMacro.updatedAt || new Date().toISOString()
        }));
      } else {
        setMacro(prev => ({
          ...prev,
          aiSummary: '',
          aiIntelligence: '',
          aiAnalysisDate: undefined
        }));
      }
    } catch (e) {
      console.error("Failed to change market mode", e);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, applyIndicators]);

  const normalizeOptimizationData = (data: any): any => {
    if (!data) return data;
    const clean = { ...data };

    // Add generation timestamp if not already present
    if (!clean.generatedAt) {
      clean.generatedAt = new Date().toISOString();
    }

    // Normalize metrics
    if (clean.metrics) {
      clean.metrics = { ...clean.metrics };
      // If expectedReturn is returned as e.g. 12.5% instead of 0.125, divide by 100.
      if (Math.abs(clean.metrics.expectedReturn) > 1.0) {
        clean.metrics.expectedReturn /= 100;
      }
      if (Math.abs(clean.metrics.expectedVolatility) > 1.0) {
        clean.metrics.expectedVolatility /= 100;
      }
    }

    // Normalize efficient frontier points
    if (Array.isArray(clean.efficientFrontier)) {
      clean.efficientFrontier = clean.efficientFrontier.map((p: any) => {
        let r = p.risk;
        let ret = p.return;
        if (Math.abs(r) > 1.0) r /= 100;
        if (Math.abs(ret) > 1.0) ret /= 100;
        return { ...p, risk: r, return: ret };
      });
    }

    return clean;
  };

  const runOptimization = useCallback(async () => {
    if (enrichedHoldings.length === 0) return;
    setIsOptimizing(true);
    setOptimizationProgress(0);
    setOptimizationStage(1); // Stage 1: Loading config
    try {
      const lang = userProfile?.analysisLanguage || userProfile?.uiLanguage || 'ko';
      const investorType = userProfile?.investorType || 3;

      const result = await optimizePortfolio(
        enrichedHoldings,
        macro,
        lang,
        investorType,
        (percent, message) => {
          setOptimizationProgress(percent);
          // Map backend percentage to UI stages for consistency with App.tsx labels
          if (percent < 40) setOptimizationStage(1);
          else if (percent < 80) setOptimizationStage(2);
          else if (percent < 100) setOptimizationStage(3);
        }
      );

      const normalizedResult = normalizeOptimizationData(result);
      setOptimizationProgress(100);
      setOptimizationStage(3); // Final parsing/complete
      setOptimizationData(normalizedResult);
      return normalizedResult;
    } catch (e) {
      console.error("Optimization failed", e);
    } finally {
      setTimeout(() => {
        setIsOptimizing(false);
        setOptimizationStage(0);
        setOptimizationProgress(0);
      }, 1000);
    }
  }, [enrichedHoldings, macro, signals, userProfile]);


  // Handle cross-market totals if needed, but for now we focus on current market

  const updateTargetWeight = useCallback(async (s: string, rawT: number) => {
    const t = rawT > 1 ? Number((rawT / 100).toFixed(4)) : Number((rawT || 0).toFixed(4));
    const market = userProfile?.targetMarket || 'US';
    const CASH_SYMBOLS = ['USD', 'KRW', 'EUR', 'JPY', 'CASH'];
    const isCash = CASH_SYMBOLS.includes(s.toUpperCase());
    
    // 🛡️ 1순위: 심볼 및 시장 정확히 일치하는 보유 자산 탐색
    const targetHolding = holdings.find(h => h.symbol.toUpperCase() === s.toUpperCase() && (h.market === market || !h.market))
      || holdings.find(h => h.symbol.toUpperCase() === s.toUpperCase())
      || (isCash ? holdings.find(h => CASH_SYMBOLS.includes(h.symbol.toUpperCase()) && h.market === market) : undefined);

    const targetSymbol = targetHolding?.symbol || s;
    const targetAccountId = targetHolding?.account_id;

    try {
      await updateHoldingInDB(targetSymbol, { targetWeight: t }, market, targetAccountId);
    } catch (err: any) {
      if (err?.message?.includes('404') || err?.status === 404 || String(err).includes('404')) {
        await saveHoldingToDB({
          symbol: targetSymbol,
          name: isCash ? (targetSymbol === 'KRW' ? 'Korean Won' : 'US Dollar') : targetSymbol,
          shares: 0,
          avgCost: 1,
          targetWeight: t,
          market
        });
      } else {
        throw err;
      }
    }
    setHoldings(prev => prev.map(h => {
      if (targetAccountId && h.account_id && h.account_id === targetAccountId && h.symbol.toUpperCase() === targetSymbol.toUpperCase()) {
        return { ...h, targetWeight: t };
      }
      if (h.symbol.toUpperCase() === targetSymbol.toUpperCase() && (h.market === market || !h.market)) {
        return { ...h, targetWeight: t };
      }
      return h;
    }));
  }, [userProfile, holdings]);

  const updateMultipleTargetWeights = useCallback(async (rawWeights: Record<string, number>) => {
    const weights: Record<string, number> = {};
    Object.entries(rawWeights).forEach(([sym, val]) => {
      weights[sym] = val > 1 ? Number((val / 100).toFixed(4)) : Number((val || 0).toFixed(4));
    });
    const market = userProfile?.targetMarket || 'US';
    const CASH_SYMBOLS = ['USD', 'KRW', 'EUR', 'JPY', 'CASH'];

    // 1. Optimistically update local holdings immediately so UI reflects AI targets right away
    setHoldings(prev => {
      const updated = [...prev];
      Object.entries(weights).forEach(([symbol, weight]) => {
        const isCash = CASH_SYMBOLS.includes(symbol.toUpperCase());
        // 🛡️ 1순위: 심볼 및 시장 일치 자산 우선 탐색
        const targetHolding = updated.find(h => h.symbol.toUpperCase() === symbol.toUpperCase() && (h.market === market || !h.market))
          || updated.find(h => h.symbol.toUpperCase() === symbol.toUpperCase())
          || (isCash ? updated.find(h => CASH_SYMBOLS.includes(h.symbol.toUpperCase()) && h.market === market) : undefined);

        const targetSymbol = targetHolding?.symbol || symbol;
        const idx = targetHolding 
          ? updated.indexOf(targetHolding) 
          : updated.findIndex(h => h.symbol.toUpperCase() === targetSymbol.toUpperCase() && (h.market === market || !h.market));

        if (idx !== -1) {
          updated[idx] = { ...updated[idx], targetWeight: weight };
        } else {
          updated.push({
            symbol: targetSymbol.toUpperCase(),
            name: isCash ? (targetSymbol === 'KRW' ? 'Korean Won' : 'US Dollar') : targetSymbol.toUpperCase(),
            shares: 0,
            avgCost: 1,
            weight: 0,
            targetWeight: weight,
            market
          });
        }
      });
      return updated;
    });

    // 2. Persist to server API gracefully
    await Promise.all(
      Object.entries(weights).map(async ([symbol, weight]) => {
        const isCash = CASH_SYMBOLS.includes(symbol.toUpperCase());
        const targetHolding = holdings.find(h => h.symbol.toUpperCase() === symbol.toUpperCase() && (h.market === market || !h.market))
          || holdings.find(h => h.symbol.toUpperCase() === symbol.toUpperCase())
          || (isCash ? holdings.find(h => CASH_SYMBOLS.includes(h.symbol.toUpperCase()) && h.market === market) : undefined);

        const targetSymbol = targetHolding?.symbol || symbol;
        const targetAccountId = targetHolding?.account_id;

        try {
          await updateHoldingInDB(targetSymbol, { targetWeight: weight }, market, targetAccountId);
        } catch (err: any) {
          if (err?.message?.includes('404') || err?.status === 404 || String(err).includes('404')) {
            try {
              await saveHoldingToDB({
                symbol: targetSymbol,
                name: isCash ? (targetSymbol === 'KRW' ? 'Korean Won' : 'US Dollar') : targetSymbol,
                shares: 0,
                avgCost: 1,
                targetWeight: weight,
                market
              });
            } catch (saveErr) {
              console.warn(`[updateMultipleTargetWeights] saveHoldingToDB fallback failed for ${targetSymbol}:`, saveErr);
            }
          } else {
            console.warn(`[updateMultipleTargetWeights] updateHoldingInDB failed for ${targetSymbol}:`, err);
          }
        }
      })
    );
  }, [userProfile, holdings]);

  // ─── Auto-Trade Log Management ──────────────────────────────────────────
  const loadAutoTradeLogs = useCallback(async () => {
    if (!userId) return;
    setIsAutoTradeLogsLoading(true);
    try {
      const logs = await fetchAutoTradeLogs({ limit: 200 });
      setAutoTradeLogs(logs);
    } catch (e) {
      console.error('[usePortfolioData] Failed to load auto-trade logs', e);
    } finally {
      setIsAutoTradeLogsLoading(false);
    }
  }, [userId]);

  const saveTradeExecution = useCallback(async (
    trades: (Omit<AutoTradeLog, 'id'> & { id?: string })[]
  ) => {
    if (!userId || trades.length === 0) return;
    const now = new Date().toISOString();
    const isLive = Boolean(userProfile?.canAccessLiveTrading);
    const isTier2Plus = ['TIER2', 'TIER3', 'TIER4', 'TIER5'].includes(String(userProfile?.membershipTier || '').toUpperCase());
    const fallbackMode = (isLive && isTier2Plus) ? 'LIVE' : (isLive ? 'LIVE' : 'PAPER');

    const newLogs: AutoTradeLog[] = trades.map(t => ({
      ...t,
      id: t.id || ('atl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)),
      executionMode: t.executionMode || (t as any).execution_mode || fallbackMode,
      executedAt: t.executedAt || now,
    }));
    // Optimistically update local state first
    setAutoTradeLogs(prev => [...newLogs, ...prev]);

    try {
      // Save to DB (fallback handled automatically inside saveAutoTradeLog)
      await Promise.all(newLogs.map(t => saveAutoTradeLog(t)));
    } catch (e) {
      console.warn('[usePortfolioData] Failed to save trade executions to server API:', e);
    }
  }, [userId, userProfile]);

  return {
    holdings: enrichedHoldings, rawHoldings: holdings, portfolioStats, macro, indicators, signals, aiAdvice, aiErrorInfo,
    fetchDeepIntelligence, fetchIntelligenceOnly, isLoading, isIntelligenceLoading, isInitialLoadDone: effectiveLoadDone, isModalOpen,
    isDbError, isAnalysisRunning, isDeepAnalysisRunning, analysisProgress, analysisStage, totalValue, dailyReturnAmt, dailyReturnPct, totalReturnAmt,
    totalReturnPct, lastUpdated, proposedTrades, userProfile: effectiveProfile, setUserProfile, refreshProfile, setIsModalOpen, addHolding, addBulkHoldings,
    updateHolding, deleteHolding, updateTargetWeight, updateMultipleTargetWeights, fetchMarketData, fetchFullMacro,
    generateAiStrategy, generateQuickSummary, stopAiAnalysis,
    changeMarketMode, macroReportFormat, setMacroReportFormat, optimizationData, isOptimizing, optimizationStage, optimizationProgress, runOptimization,
    displayCurrencyPreference, setCurrencyPreference, isMacroLoading, currentExchangeRate,
    liquidateHolding,
    watchlist, addWatchlist, removeWatchlist,
    selectedAccountId, setSelectedAccountId,
    autoTradeLogs, isAutoTradeLogsLoading, loadAutoTradeLogs, saveTradeExecution,
  };
}
